import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, GitMerge, Zap, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Bump this constant whenever a new release ships.
// The banner shows once per user per version — stored in localStorage.
const CURRENT_VERSION = 'v1.2.0';
const STORAGE_KEY = `seen_update_${CURRENT_VERSION}`;

const UpdateNotification: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isId = i18n.language === 'id';

  useEffect(() => {
    // Only show if user is logged in and hasn't seen this version yet
    const token = localStorage.getItem('auth_token');
    const seen = localStorage.getItem(STORAGE_KEY);
    if (token && !seen) {
      // Small delay so it doesn't flash during page load
      const t = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(t);
    }
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
  };

  const goToChangelog = () => {
    dismiss();
    navigate('/changelog');
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 80, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 80, scale: 0.95 }}
          transition={{ type: 'spring', damping: 20, stiffness: 280 }}
          className="fixed bottom-6 right-6 z-[9999] w-80 bg-[#0D0D0D] border border-neutral-800 rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Red top accent */}
          <div className="h-0.5 bg-gradient-to-r from-[#E50914] via-[#ff6b6b] to-transparent" />

          <div className="p-5">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-[#E50914]/10 border border-[#E50914]/20 flex items-center justify-center flex-shrink-0">
                  <GitMerge size={15} className="text-[#E50914]" />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-[#E50914]">
                    {isId ? 'Pembaruan Tersedia' : 'Update Available'}
                  </p>
                  <p className="text-sm font-black text-white tracking-tight">{CURRENT_VERSION}</p>
                </div>
              </div>
              <button
                onClick={dismiss}
                className="p-1 text-neutral-600 hover:text-white transition-colors rounded-lg hover:bg-neutral-800"
              >
                <X size={14} />
              </button>
            </div>

            {/* Highlights */}
            <div className="space-y-1.5 mb-4">
              {(isId ? [
                'Live On-Chain Wallet Balance (EVM, SOL, BTC)',
                'Selective Sync & CEX Switcher Filter',
                'Dynamic SEO Meta per halaman',
                'Static HTML Route Prerendering',
                'Fix Tabrakan Cache Identifikasi Akun',
              ] : [
                'Live On-Chain Wallet Balance (EVM, SOL, BTC)',
                'Selective Sync & CEX Switcher Filter',
                'Dynamic SEO Meta per Page',
                'Static HTML Route Prerendering',
                'Fix Account ID Cache Collision',
              ]).map(item => (
                <div key={item} className="flex items-start gap-2">
                  <Zap size={10} className="text-[#E50914] flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-neutral-400 leading-tight">{item}</p>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={goToChangelog}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#E50914] hover:bg-[#ff2020] text-white text-xs font-black rounded-xl transition-colors"
              >
                <ExternalLink size={12} />
                {isId ? 'Lihat Detail' : 'View Details'}
              </button>
              <button
                onClick={dismiss}
                className="px-4 py-2 bg-neutral-900 hover:bg-neutral-800 text-neutral-400 text-xs font-bold rounded-xl transition-colors border border-neutral-800"
              >
                {isId ? 'Nanti' : 'Later'}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UpdateNotification;
